import * as yup from "yup";
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { FaCircleNotch } from "react-icons/fa";
import { IoSaveOutline } from "react-icons/io5";

import { paymentService as payment } from "./../../../services/paymentService";
import { paymentConfirmationServices as confirmation } from "./../../../services/paymentConfirmationServices";

import ReactSelect from "./../../../components/common/ReactSelect";
import Textbox from "./../../../components/common/Textbox";
import Textarea from "./../../../components/common/Textarea";
import Datepicker from "./../../../components/common/Datepicker";
import Toaster from "../../../components/common/Toaster";

import InnerImageZoom from "react-inner-image-zoom";
import moment from "moment";

const schema = yup.object({
  email_send: yup.bool(),
  amount: yup.string().required("Please enter amount."),
  transaction_date: yup.string().required("Please select transaction date."),
  status: yup.object().shape({
    label: yup.string().required("Please select status."),
    value: yup.string().required("Please select status."),
  }),
  payment_type: yup.object().shape({
    label: yup.string().required("Please select payment type."),
    value: yup.string().required("Please select payment type."),
  }),
});

const PaymentForm = ({ data, onSuccess, isLoading, setLoading, setShow }) => {
  const paymentTypes = payment.getPaymentTypes();
  const statuses = confirmation.getPaymentConfirmationStatuses();

  const [transactionDate, setTransactionDate] = useState(
    Date.parse(moment(new Date(), "MM-DD-yyyy").toISOString())
  );

  const {
    register,
    handleSubmit,
    setValue,
    clearErrors,
    control,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema) });

  useEffect(() => {
    if (data) {
      const payment_date = moment(data.transaction_date).format("MM-DD-yyyy");

      setValue("account_id", data.account_id);
      setValue("accounting_invoice_id", data.accounting_invoice_id);
      setValue("payment_confirmation_id", data.id);
      setValue("reference_number", data.reference_number);
      setValue("amount", data.amount_value);
      setValue("remarks", data.remarks);
      setValue("transaction_date", payment_date);
      setTransactionDate(new Date(payment_date));
    }
  }, [data, setValue]);

  const onSubmit = async (info) => {
    try {
      setLoading(true);

      const item = { ...info };
      item["payment_type"] = info.payment_type.value;
      item["status"] = info.status.value;
      item["status_type"] = info.status.label;

      await confirmation.createPaymentFromConfirmationAsync(item);

      Toaster.Success("Payment Created Successfully");

      onSuccess(item);
      setShow(false);
      setLoading(false);
    } catch (ex) {
      setLoading(false);
      Toaster.Error("Something went wrong.");
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="p-8 space-y-6">
      <div className="grid grid-cols-2 gap-16">
        <div className="col-span-1">
          <div className="border">
            {data && (
              <InnerImageZoom
                src={data.s3_reference_name}
                zoomSrc={data.s3_reference_name}
                hideHint={true}
              />
            )}
          </div>
        </div>
        <div className="col-span-1 space-y-6">
          <div className="grid grid-cols-2">
            <div className="col-span-2">
              <ReactSelect
                id="statuses-dropdown"
                name="status"
                title="Payment Confirmation Status"
                options={statuses}
                control={control}
                register={register("status")}
                errors={errors}
              />
            </div>
          </div>
          <div className="grid grid-cols-2">
            <div className="col-span-2">
              <hr />
            </div>
          </div>
          <div className="grid grid-cols-2">
            <div className="col-span-2">
              {data && (
                <div className="card p-3">
                  <div className="flex justify-between">
                    <div>
                      <div className="text-sm text-gray-500 mb-2 flex gap-2">
                        No. {data.invoice_number}{" "}
                        {data.is_paid && (
                          <div className="bg-green-400 px-2 text-white text-xs my-auto rounded">
                            Paid
                          </div>
                        )}
                        {!data.is_paid && (
                          <div className="bg-red-400 px-2 text-white text-xs my-auto rounded">
                            Unpaid
                          </div>
                        )}
                      </div>
                      <span className="text-sm text-gray-600">
                        {moment(data.period_start).format("LL")} to{" "}
                        {moment(data.period_end).format("LL")}
                      </span>
                    </div>
                    <p className="text-green-400">{data.invoice_amount}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div className="col-span-2">
              <Textbox
                id="reference-number-textbox"
                name="reference_number"
                type="text"
                title="Reference No."
                register={register("reference_number")}
                errors={errors}
                optional
              />
            </div>
          </div>
          <div className="grid grid-cols-2 space-x-4">
            <div className="col-span-1">
              <ReactSelect
                id="payment-type-dropdown"
                name="payment_type"
                title="Payment Type"
                options={paymentTypes}
                control={control}
                register={register("payment_type")}
                errors={errors}
              />
            </div>
            <div className="col-span-1">
              <Textbox
                id="amount-textbox"
                name="amount"
                type="text"
                title="Amount"
                register={register("amount")}
                errors={errors}
              />
            </div>
          </div>
          <div className="grid grid-cols-2">
            <div className="col-span-1">
              <Datepicker
                id="transaction-datepicker"
                name="transaction_date"
                title="Transaction Date"
                dateFormat="MM-DD-yyyy"
                timeFormat={false}
                setValue={setValue}
                clearErrors={clearErrors}
                initialValue={transactionDate}
                value={transactionDate}
                onDateChange={setTransactionDate}
                register={register("transaction_date")}
                errors={errors}
              />
            </div>
          </div>
          <div className="grid grid-cols-2">
            <div className="col-span-2">
              <input
                id="email-send-checkbox"
                name="email_send"
                type="checkbox"
                defaultChecked={true}
                {...register("email_send")}
                className="text-blue-500 cursor-pointer border-gray-300 rounded dark:border-gray-600"
              />
              <label className="ml-3 text-sm text-gray-400">
                Send via Email
              </label>
            </div>
          </div>
          <div className="grid grid-cols-2">
            <div className="col-span-2">
              <Textarea
                id="remarks-textarea"
                name="remarks"
                title="Remarks"
                placeholder="Say something..."
                register={register("remarks")}
                errors={errors}
                rows={4}
                optional
              />
            </div>
          </div>
          <div className="grid grid-2 pt-2">
            <div className="col-span-2">
              <button
                type="submit"
                className="button button-primary disabled:opacity-70 disabled:cursor-not-allowed"
                disabled={isLoading}
              >
                {isLoading && <FaCircleNotch className="animate-spin mr-3" />}
                {!isLoading && <IoSaveOutline className="mr-3" />}
                Create Payment
              </button>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};

export default PaymentForm;
